import { call, takeEvery, put, select, take } from 'redux-saga/effects'
import buyServices from '@buy/services'
import { getPlanNameFromPath, getProductNameFromPath } from '@common/utils/path-utils'
import { buyPlanActions, BuyPlanActionTypes } from '@buy/state/plan-data/actions'
import { VIEW_PATH } from '@common/constants'
import { flexActions } from '@common/state/flex/actions'
import { getGUID } from '@common/utils/getGUID'
import { fatalErrorActions } from '@common/state/fatal-error/actions'
import { locationActions, LocationActionTypes } from '@common/state/location/actions'
import { GenericResponse } from '@common/state/generic-response/generic-response'
import { BuyPlan, PriceInfo } from '@buy/state/plan'
import { getLocationState } from '@common/state/selectors'
import { LocationState } from '@common/state/location/state'
import { isEmpty } from 'lodash-es'
import { getPricingInfo, mapPricingInfo } from './content-helpers'

function* loadContentSaga() {
    try {
        // According to the documentation, we should generate a new unique id for the cybersource fingerprint on every page load
        // https://confluence.ops.expertcity.com/x/L4UVIg
        // https://confluence.ops.expertcity.com/x/7um3Cw
        // https://confluence.ops.expertcity.com/x/cxthC
        const guid = getGUID()
        yield put(flexActions.setFingerPrint(guid))

        const planName = getPlanNameFromPath(window.location.pathname).toLowerCase()
        const contentEndpoint = getProductNameFromPath(window.location.pathname, false).toLowerCase()
        const labelServerResponse: GenericResponse = yield call(
            buyServices.getBuyPlanData,
            contentEndpoint,
            planName.replaceAll('-', '')
        )
        if (labelServerResponse && labelServerResponse.data && isEmpty(labelServerResponse.data.error)) {
            const { labels, productSettings, plan, ...content } = labelServerResponse.data
            const { sEOPageTitle } = labels
            yield call(() => {
                if (sEOPageTitle) document.title = sEOPageTitle
            })

            // Wait for the location info call to finish so we have the currencyRestrictions
            // (it get triggered on src/products/buy/views/Billing/index.tsx,
            // and calls the saga src/common/sagas/location/location-info.ts)
            const { isLocationInfoLoaded }: LocationState = yield select(getLocationState)
            if (!isLocationInfoLoaded) {
                yield take(LocationActionTypes.SET_IS_LOCATION_INFO_LOADED)
            }

            // yield put(buyPlanActions.setBuyPlan(content, { ...labels }, { ...plan }, { ...productSettings }))
            const { locationInfo }: LocationState = yield select(getLocationState)

            // Get pricing info
            // usually we only need pricing info for one currency, but if pricing for the main currency isn't included
            // in the price-info response (such is the case with MXN for GoToMeeting), then we'll need to fallback, so
            // just go ahead and get price info for all of the currencies listed in currencyRestrictions.
            // To see where currencyRestrictions is set, see src/common/sagas/location/location-info.ts
            const currencies = locationInfo.currencyRestrictions
            const priceInfoResponse: PriceInfo[] = yield call(getPricingInfo, plan, currencies)
            if (priceInfoResponse && priceInfoResponse.length > 0) {
                const primaryCurrency = currencies[0]
                const priceInfoHasUserCurrency = priceInfoResponse.every(
                    (info) => info.pricingInfo && info.pricingInfo[primaryCurrency]
                )
                const displayCurrency = priceInfoHasUserCurrency ? primaryCurrency : currencies[1]
                yield put(
                    locationActions.setLocationInfo({
                        ...locationInfo,
                        currency: displayCurrency,
                    })
                )

                const newPlan: BuyPlan = mapPricingInfo(plan, priceInfoResponse)
                yield put(buyPlanActions.setBuyPlan(content, { ...labels }, { ...newPlan }, { ...productSettings }))
            }
            yield put(buyPlanActions.setContentLoaded(true))
        } else {
            window.location.assign(VIEW_PATH.PATH_BASE)
        }
    } catch (e) {
        yield put(fatalErrorActions.setFatalError(true, { trackErrorMessage: 'label data failed to load' }))
    }
}

function* initializeBuyLoadContentSaga() {
    yield takeEvery(BuyPlanActionTypes.GET_LABEL_CONTENT, loadContentSaga)
}

export default initializeBuyLoadContentSaga
